import React, { useState, useEffect } from 'react';
import styles from '../../styles/TopUsersWidget.module.css';
import { 
  Paper, 
  Typography, 
  Box, 
  List, 
  ListItem, 
  ListItemText, 
  Divider,
  CircularProgress
} from '@mui/material';
import AuditServices from '../../../../services/audit.services';
import logger from '../../../../utils/logger';
const TopUsersWidget = ({ module, startDate, endDate }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchTopUsers = async () => {
      setLoading(true);
      try {
        const data = await AuditServices.getTopUsers(module, startDate, endDate);
        setUsers(data);
      } catch (error) {
        logger.error(`Failed to fetch top users for ${module}:`, error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (module) {
      fetchTopUsers();
    }
  }, [module, startDate, endDate]);
  
  const getDisplayName = (username) => {
    if (!username) return 'Unknown User';
    
    if (username.includes('@')) {
      // Extract the name part (before @) from the email
      const namePart = username.split('@')[0];
      
      // Split by dot and capitalize each part
      return namePart.split('.')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
    
    return username;
  };
  
  return (
    <Paper elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight="bold" className={styles.title}>
          Top 20 {module} Users
        </Typography>
      </Box>
      
      <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 300 }}>
        {loading ? (
          <Box className={styles.loadingContainer}>
            <CircularProgress size={24} sx={{ color: '#1D9999' }} />
          </Box>
        ) : users.length > 0 ? (
          <List dense>
            {users.map((user, index) => (
              <React.Fragment key={user.username || index}>
                <ListItem className={styles.listItem}>
                  <Box display="flex" alignItems="center">
                    <Box 
                      className={`${styles.userRank} ${
                        index === 0 ? styles.rankOne : 
                        index === 1 ? styles.rankTwo : 
                        index === 2 ? styles.rankThree : ''
                      }`}
                    >
                      {index + 1}
                    </Box>
                    <ListItemText 
                      primary={getDisplayName(user.username)}
                      secondary={<span className={styles.userCount}>{user.count} hits</span>}
                    />
                  </Box>
                </ListItem>
                {index < users.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No data available
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const TopUsersWidgetGrid = ({ modules, startDate, endDate }) => {
  if (!modules || modules.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Top 20 Users by Module</Typography>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No modules data available
          </Typography>
        </Box>
      </Paper>
    );
  }
  
  return (
    <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Top 20 Users by Module</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
        {modules.map((module) => (
          <TopUsersWidget 
            key={module} 
            module={module} 
            startDate={startDate} 
            endDate={endDate} 
          />
        ))}
      </Box>
    </Paper>
  );
};

export default TopUsersWidgetGrid;